import React from 'react';
import { createContext } from 'use-context-selector';
import { useAuth } from '@auth-wrapper';
import {
  createOptimizedScenario as createOptimizedScenarioAPI,
  type OptimizedScenarioParams,
} from '@services/scenarios-simulator';
import { notifyError } from '@components/atoms/toast/services';

interface OptimizedScenarioContextData {
  createOptimizedScenario: (params: OptimizedScenarioParams) => Promise<void>;
  isLoadingCreateOptimizedScenario: boolean;
  optimizedScenario: Record<string, unknown>;
}

export const OptimizedScenarioContext = createContext({} as OptimizedScenarioContextData);

interface Provider {
  children: React.ReactNode;
}

export type { OptimizedScenarioParams as CreateOptimizedScenarioParms };

const OptimizedScenarioProvider: React.FC<Provider> = ({ children }) => {
  const {
    authStatus: { accessToken: token },
  } = useAuth();

  const [optimizedScenario, setOptimizedScenario] = React.useState<Record<string, unknown>>({});
  const [isLoadingCreateOptimizedScenario, setIsLoadingCreateOptimizedScenario] = React.useState<boolean>(false);

  const createOptimizedScenario = React.useCallback(
    async (params: OptimizedScenarioParams): Promise<void> => {
      if (!token) {
        notifyError('No authentication token available');
        return;
      }

      setIsLoadingCreateOptimizedScenario(true);
      try {
        const simulation = await createOptimizedScenarioAPI(params, token);
        setOptimizedScenario(simulation);
      } catch (error) {
        notifyError('Error creating optimized scenario');
        throw error;
      } finally {
        setIsLoadingCreateOptimizedScenario(false);
      }
    },
    [token],
  );

  return (
    <OptimizedScenarioContext.Provider
      value={{
        createOptimizedScenario,
        isLoadingCreateOptimizedScenario,
        optimizedScenario,
      }}
    >
      {children}
    </OptimizedScenarioContext.Provider>
  );
};

export default OptimizedScenarioProvider;
